import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Subject, Subscription, take } from 'rxjs';
import { AdminRestService } from '../services/rest/admin-rest.service';
import { UserService } from '../services/user.service';
import { ResponseItem } from '@models/response-item.model';

/**
 * Base class for page components that manage CRUD operations for entities
 * @template T The type of entity being managed
 */
@Component({
  template: '' // Abstract component, no template
})
export abstract class BasePageComponent<T extends { id: string }> implements OnInit, OnDestroy {
  SelectedItem?: T;
  SelectedItemEtag?: string;
  IsViewing: boolean = true;
  IsEditing: boolean = false;
  AllowInteraction: boolean = false;
  CanBeEdited: boolean = false;
  public isSuperAdmin: boolean = false;
  public isShared: boolean = false;
  public listHidden: boolean = false;
  public IsCreating: boolean = false;

  changeViewSubject: Subject<ResponseItem<T> | undefined> = new Subject<ResponseItem<T> | undefined>();
  itemDeletedSubject: Subject<T> = new Subject<T>();

  userServiceSubscription: Subscription;

  protected hasCommandPermissions: boolean = false;

  constructor(
    protected readonly adminApi: AdminRestService,
    protected readonly confirmationService: ConfirmationService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly userService: UserService
  ) {
    this.userServiceSubscription = this.userService.UserInformation.subscribe(userInfo => {
      if (!userInfo) {
        return;
      }
      this.isSuperAdmin = userInfo.isSuperAdmin;
    });
    this.hasCommandPermissions = this.userService.hasCommandPermissions();
  }

  get canEdit(): boolean {
    return this.AllowInteraction && !!this.SelectedItem && this.IsViewing && !this.isShared && this.CanBeEdited && this.hasCommandPermissions;
  }

  get canDelete(): boolean {
    return this.AllowInteraction && !!this.SelectedItem && !this.isShared && this.CanBeEdited && this.hasCommandPermissions;
  }

  ngOnInit(): void {
    this.adminApi.getCustomersConfiguration().pipe(take(1))
      .subscribe((data) => {
        this.CanBeEdited = data.Item ?? false;
      });
  }

  ngOnDestroy(): void {
    this.userServiceSubscription.unsubscribe();
  }

  openDeleteConfirm(header: string, message: string): void {
    this.confirmationService.confirm({
      header,
      message,
      acceptIcon: 'ic_trash',
      rejectIcon: 'ic_remove',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: () => this.deleteItem(),
    });
  }

  public onItemSelected(item: T): void {
    if (this.IsEditing) {
      return;
    } else {
      this.SelectedItem = item;
    }
  }

  public selectItem(item: T): void {
    this.onItemSelected(item);
    this.IsCreating = false;
    this.listHidden = true;
  }

  public addNewItemClicked(): void {
    this.IsViewing = false;
    this.IsEditing = true;
    this.SelectedItem = undefined;
    this.IsCreating = true;
    this.listHidden = true;
  }

  public setInteractionState(): void {
    // Override in child classes if needed
  }

  public interactionEnabled(): void {
    this.AllowInteraction = true;
  }

  public interactionDisabled(): void {
    this.AllowInteraction = false;
  }

  public onEditClicked(etag?: string): void {
    this.listHidden = true;
    this.IsCreating = false;
    if (etag && this.SelectedItem) {
      // The View page already fetched the up-to-date ETag, so reuse it directly
      // rather than re-fetching the same record before entering edit mode.
      this.SelectedItemEtag = etag;
      this.IsViewing = false;
      this.IsEditing = true;
      return;
    }
    if (!this.SelectedItem) {
      return;
    }
    this.interactionDisabled();
    this.loadItemForEdit(this.SelectedItem.id);
  }

  public onSubmitEvent(responseItem: ResponseItem<T>): void {
    this.listHidden = true;
    this.AllowInteraction = true;
    if (responseItem) {
      this.SelectedItem = responseItem.Item;
      this.SelectedItemEtag = responseItem.ETag;
    }
    this.IsViewing = true;
    this.IsEditing = false;
    this.IsCreating = false;
    this.changeViewSubject.next(responseItem);
  }

  public onCancelEvent(): void {
    if (this.IsCreating) {
      this.IsCreating = false;
      this.showList();
      return;
    }
    this.IsViewing = true;
    this.IsEditing = false;
    this.changeViewSubject.next(undefined);
    this.listHidden = true;
  }

  public showList(): void {
    this.listHidden = false;
    this.IsEditing = false;
    this.IsViewing = true;
    this.SelectedItem = undefined;
    this.SelectedItemEtag = undefined;
  }

  public onViewRequest(isInteractionAllowed: boolean): void {
    this.AllowInteraction = isInteractionAllowed;
  }

  public setShared(event: any): void {
    this.isShared = event;
    this.AllowInteraction = true;
    this.cdr.detectChanges();
  }

  // Abstract methods that child classes must implement
  protected abstract deleteItem(): void;
  protected abstract loadItemForEdit(id: string): void;
  protected abstract onRefreshClicked(): void;
}

